import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

// Helper function to get color based on biller name
const getBillerColor = (billerName) => {
    const colors = ["#0277BD", "#01579B", "#006064", "#004D40", "#1B5E20", "#E65100", "#B71C1C", "#311B92", "#4527A0", "#F57F17", "#0D47A1", "#BF360C", "#2E7D32", "#C62828", "#283593"];
    let hash = 0;
    for (let i = 0; i < billerName.length; i++) {
        hash = billerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

const getBillerLogo = (billerName) => {
    const words = billerName.split(' ');
    if (words.length === 1) {
        return billerName.substring(0, 2).toUpperCase();
    }
    return (words[0].charAt(0) + (words[1].charAt(0) || words[0].charAt(1))).toUpperCase();
};

function OperatorForm() {
    const navigate = useNavigate();
    const location = useLocation();

    // Get operator data from location state
    const { operator_id, biller_name, category, logo } = location.state || {};

    const [formData, setFormData] = useState({});
    const [isFetching, setIsFetching] = useState(false);
    const [billDetails, setBillDetails] = useState(null);
    const [step, setStep] = useState("input"); // 'input' or 'fetched'
    const [formFields, setFormFields] = useState([]);
    const [loadingForm, setLoadingForm] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [inputSLNumber, setInputSLNumber] = useState("");
    const [paymentAmount, setPaymentAmount] = useState("");
    const [amountError, setAmountError] = useState("");
    const [range, setRange] = useState({ min: 100, max: 100000 });

    // Fetch operator form on component mount
    useEffect(() => {
        if (!operator_id) {
            toast.error("No operator selected");
            navigate(-1);
            return;
        }

        const fetchOperatorForm = async () => {
            setLoadingForm(true);
            try {
                const result = await apiCall(
                    "operator-form",
                    {
                        operator_id: operator_id
                    },
                    true
                );

                if (result.status === true && result.response_code === 1) {
                    const forms = Array.isArray(result.operator_form) ? result.operator_form : [result.operator_form];

                    if (forms.length > 0) {
                        const form = forms[0];
                        setInputSLNumber(form.sl);
                        initializeFormFields(form);
                    } else {
                        toast.error("No form configuration found");
                    }
                } else {
                    toast.error(result.message || "Failed to load form data");
                }
            } catch (error) {
                console.error("Error fetching operator form:", error);
                toast.error("Failed to load form data");
            } finally {
                setLoadingForm(false);
            }
        };

        fetchOperatorForm();
    }, [operator_id, navigate]);

    // Validate form whenever formData or formFields change
    useEffect(() => {
        validateForm();
    }, [formData, formFields]);

    const initializeFormFields = (form) => {
        const nparam = parseInt(form.nparam, 10) || 0;
        const dynamicFields = [];
        const initialFormData = {};

        for (let i = 1; i <= nparam; i++) {
            const paramKey = `param${i}`;
            const paramLabel = form[`${paramKey}_label`];
            const paramType = form[`${paramKey}_type`];
            const paramValue = form[`${paramKey}_value`];
            const paramIsOptional = form[`${paramKey}_isOptional`];
            const paramMin = form[`${paramKey}_min`];
            const paramMax = form[`${paramKey}_max`];
            const paramRegex = form[`${paramKey}_regular_ex`];
            const paramIsHidden = form[`${paramKey}_isHidden`];
            const paramIsPrimary = form[`${paramKey}_isPrimary`];
            const paramIsAdditional = form[`${paramKey}_isAdditional`];

            // Skip if label is empty or field is hidden
            if (!paramLabel || paramLabel.trim() === '' || paramIsHidden === "1") continue;

            // Check if field is Dropdown type
            const isDropdown = paramType === "Dropdown";

            // Extract options from param_value for dropdown fields
            let options = [];
            if (isDropdown && paramValue) {
                try {
                    const parsedValue = JSON.parse(paramValue);
                    if (Array.isArray(parsedValue)) {
                        options = parsedValue;
                    } else {
                        options = paramValue.split(',').map(opt => opt.trim());
                    }
                } catch (error) {
                    options = paramValue.split(',').map(opt => opt.trim());
                }
            }

            // Determine field type
            let fieldType = "text";
            if (isDropdown) {
                fieldType = "select";
            } else if (paramType === "Number" || paramType === "Numeric") {
                fieldType = "number";
            } else if (paramType === "Date") {
                fieldType = "date";
            } else if (paramType === "Email") {
                fieldType = "email";
            } else if (paramType === "Phone") {
                fieldType = "tel";
            }

            // Check if field is required (isOptional: "0" means required)
            const isRequired = paramIsOptional === "0" || paramIsOptional === 0;

            dynamicFields.push({
                name: paramKey,
                key: paramKey,
                label: paramLabel,
                placeholder: `Enter ${paramLabel}`,
                type: fieldType,
                required: isRequired,
                regex: paramRegex || null,
                minLength: parseInt(paramMin, 10) || 0,
                maxLength: parseInt(paramMax, 10) || 0,
                options: options,
                isDropdown: isDropdown,
                isHidden: paramIsHidden === "1",
                isPrimary: paramIsPrimary === "1",
                isAdditional: paramIsAdditional === "1",
                paramType: paramType,
                shouldUppercase: fieldType === "text" || fieldType === "tel"
            });

            initialFormData[paramKey] = '';
        }

        setFormFields(dynamicFields);
        setFormData(initialFormData);
    };

    const validateForm = () => {
        let isValid = true;

        if (formFields.length === 0) {
            setIsFormValid(false);
            return;
        }

        for (const field of formFields) {
            const value = formData[field.key] || '';
            const valueString = String(value);

            // Check required fields
            if (field.required && !valueString.trim()) {
                isValid = false;
                break;
            }

            // For dropdowns, check if a valid option is selected
            if (field.isDropdown && field.required && (!valueString || valueString.trim() === '')) {
                isValid = false;
                break;
            }

            // Skip further validation if value is empty and field is not required
            if (!field.required && !valueString.trim()) {
                continue;
            }

            // Validate regex pattern
            if (field.regex && valueString && field.regex.trim() !== "") {
                try {
                    const regex = new RegExp(field.regex);
                    if (!regex.test(valueString)) {
                        isValid = false;
                        break;
                    }
                } catch (error) {
                    console.error('Invalid regex pattern:', field.regex);
                }
            }

            // Validate min/max length for text fields
            if (!field.isDropdown && field.type !== "number" && field.type !== "date") {
                if (field.minLength > 0 && valueString.length < field.minLength) {
                    isValid = false;
                    break;
                }
                if (field.maxLength > 0 && valueString.length > field.maxLength) {
                    isValid = false;
                    break;
                }
            }

            // Validate number fields
            if (field.type === "number" && valueString) {
                const numValue = parseFloat(valueString);
                if (isNaN(numValue)) {
                    isValid = false;
                    break;
                }

                // Check digit length for numbers
                const digitLength = valueString.replace(/^0+/, '').length || valueString.length;
                if (field.minLength > 0 && digitLength < field.minLength) {
                    isValid = false;
                    break;
                }
                if (field.maxLength > 0 && digitLength > field.maxLength) {
                    isValid = false;
                    break;
                }
            }
        }

        setIsFormValid(isValid);
    };

    const handleInputChange = (key, value, shouldUppercase = false) => {
        // Convert to uppercase if needed
        let processedValue = value;
        if (shouldUppercase && typeof value === 'string') {
            processedValue = value.toUpperCase();
        }

        setFormData(prev => ({
            ...prev,
            [key]: processedValue
        }));
    };

    const handleAmountChange = (value) => {
        // Remove any non-numeric characters except decimal
        let cleanValue = value.replace(/[^0-9.]/g, '');

        // Handle multiple decimal points
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
            cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }

        setPaymentAmount(cleanValue);
        validateAmount(cleanValue);
    };

    const validateAmount = (value) => {
        if (!value || value === '') {
            setAmountError('');
            return;
        }

        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            setAmountError('Please enter a valid amount');
            return;
        }

        const min = range.min || 100;
        const max = range.max || 100000;

        if (numValue < min) {
            setAmountError(`Minimum amount is ₹${min}`);
        } else if (numValue > max) {
            setAmountError(`Maximum amount is ₹${max}`);
        } else {
            setAmountError('');
        }
    };

    const handleFetchBill = async () => {
        if (!isFormValid) {
            toast.error("Please fill all required fields correctly");
            return;
        }

        setIsFetching(true);

        try {
            const result = await apiCall(
                "bill-fetch",
                {
                    operator_id: operator_id,
                    form: formData,
                    input_sl_number: inputSLNumber,
                },
                true
            );

            console.log("Bill Fetch Response:", result);

            if (result.status === true && result.response_code === 1) {
                const billInfo = result.bill_info || {};
                if (billInfo.acceptPayment) {
                    var additional = {};

                    const additionalInfo = result.additional?.bill_fetch_info?.additionalInfo?.info || [];
                    if (Array.isArray(additionalInfo) && additionalInfo.length > 0) {
                        additional = additionalInfo;
                    }

                    navigate("/payment-confirmation", {
                        state: {
                            service: "Bill Payment",
                            operator: biller_name,
                            operator_id: operator_id,
                            number: billInfo.cellNumber || "",
                            amount: billInfo.billnetamount || result.range.min,
                            consumerName: billInfo.userName || "",
                            serviceSpecificData: {
                                range: result.range,
                                acceptPartPay: billInfo.acceptPartPay !== false,
                                billNumber: "",
                                billDate: billInfo.billDate || "",
                                dueDate: billInfo.dueDate || "",
                                billAmount: billInfo.billAmount || "",
                                billConvenienceFee: billInfo.bill_convenience_fee || "",
                                billNetAmount: billInfo.billnetamount || result.range.min,
                                additional: additional,
                                pass_data: result.additional,
                                form: formData
                            }
                        }
                    });
                } else {
                    toast.error(result.message || "Failed to fetch bill");
                }
            } else {
                toast.error(result.message || "Failed to fetch bill");
            }
        } catch (error) {
            console.error("Fetch bill error:", error);
            toast.error("Network error. Please try again.");
        } finally {
            setIsFetching(false);
        }
    };

    const handlePay = () => {
        const amount = parseFloat(paymentAmount);
        if (!amount || amount <= 0) {
            toast.error("Please enter a valid payment amount");
            return;
        }

        const min = range.min || 100;
        const max = range.max || 100000;

        if (amount < min) {
            toast.error(`Minimum payment amount is ₹${min}`);
            return;
        }
        if (amount > max) {
            toast.error(`Maximum payment amount is ₹${max}`);
            return;
        }

        navigate("/confirm-payment", {
            state: {
                type: "bill",
                description: `${category} Bill - ${biller_name}`,
                amount: amount,
                operator: biller_name,
                operator_id: operator_id,
                formData: formData,
                billDetails: billDetails,
                input_sl_number: inputSLNumber,
                paymentAmount: amount
            }
        });
    };

    const renderFormField = (field) => {
        if (field.isDropdown && field.options && field.options.length > 0) {
            return (
                <div className="relative">
                    <select
                        value={formData[field.key] || ''}
                        onChange={(e) => handleInputChange(field.key, e.target.value, false)}
                        className="w-full p-3.5 rounded-xl text-[15px] font-semibold outline-none transition-all duration-200 appearance-none cursor-pointer"
                        style={{
                            border: `1px solid ${APP_THEME.borderColor}`,
                            background: APP_THEME.surfaceColor,
                            color: APP_THEME.textPrimary,
                            fontFamily: APP_THEME.fontFamily,
                            textTransform: 'none'
                        }}
                        required={field.required}
                    >
                        <option value="" disabled>
                            Select {field.label}
                        </option>
                        {field.options.map((option, index) => (
                            <option key={index} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <div
                        className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none"
                        style={{ color: APP_THEME.textSecondary }}
                    >
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                        </svg>
                    </div>
                </div>
            );
        }

        const inputProps = {
            type: field.type,
            value: formData[field.key] || '',
            onChange: (e) => handleInputChange(field.key, e.target.value, field.shouldUppercase),
            placeholder: field.placeholder,
            className: `w-full p-3.5 rounded-xl text-[15px] font-semibold outline-none transition-all duration-200`,
            style: {
                border: `1px solid ${APP_THEME.borderColor}`,
                background: APP_THEME.surfaceColor,
                color: APP_THEME.textPrimary,
                fontFamily: APP_THEME.fontFamily,
                textTransform: field.shouldUppercase ? 'uppercase' : 'none'
            },
            onFocus: (e) => (e.target.style.borderColor = APP_THEME.primaryColor),
            onBlur: (e) => (e.target.style.borderColor = APP_THEME.borderColor),
            required: field.required
        };

        if (field.type === "number") {
            if (field.minLength > 0) inputProps.min = field.minLength;
            if (field.maxLength > 0) inputProps.max = field.maxLength;
        }

        if (field.type === "text" || field.type === "email" || field.type === "tel") {
            if (field.minLength > 0) inputProps.minLength = field.minLength;
            if (field.maxLength > 0) inputProps.maxLength = field.maxLength;
        }

        if (field.regex && field.regex.trim() !== "" && field.type !== "date" && field.type !== "number") {
            inputProps.pattern = field.regex;
        }

        return <input {...inputProps} />;
    };

    const renderInputForm = () => {
        const info = {
            logo: logo ? null : getBillerLogo(biller_name),
            color: getBillerColor(biller_name)
        };

        return (
            <div>
                {/* Operator Info Card */}
                <div className="flex items-center gap-3 mb-6 p-3.5 px-4 rounded-xl border"
                    style={{
                        background: APP_THEME.surfaceColor,
                        borderColor: APP_THEME.borderColor,
                    }}
                >
                    {logo ? (
                        <img
                            src={logo}
                            alt={biller_name}
                            className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                }
                            }}
                        />
                    ) : null}
                    <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-extrabold text-white flex-shrink-0"
                        style={{
                            background: info.color,
                            fontFamily: APP_THEME.fontFamily,
                            display: logo ? 'none' : 'flex'
                        }}
                    >
                        {info.logo}
                    </div>
                    <div className="flex-1">
                        <div className="text-[15px] font-bold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            {biller_name}
                        </div>
                        <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            {category} Provider
                        </div>
                    </div>
                </div>

                {/* Dynamic Form Fields */}
                {formFields.length > 0 ? (
                    <>
                        {formFields.map((field) => (
                            <div key={field.key} className="mb-5">
                                <label className="block text-sm font-semibold mb-2" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                                    {field.label}
                                    {field.required && <span style={{ color: APP_THEME.errorColor }}> *</span>}
                                </label>
                                {renderFormField(field)}
                            </div>
                        ))}

                        {/* Fetch Bill Button */}
                        <button
                            onClick={handleFetchBill}
                            disabled={isFetching || !isFormValid}
                            className="w-full h-12 text-white border-none rounded-lg text-base font-bold cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                            style={{
                                background: APP_THEME.primaryColor,
                                borderRadius: APP_THEME.borderRadiusBtn,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            {isFetching ? (
                                <>
                                    <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid #ffffff`, borderTopColor: "transparent" }} />
                                    <span>Fetching Bill...</span>
                                </>
                            ) : (
                                "Fetch Bill"
                            )}
                        </button>
                    </>
                ) : (
                    <div className="text-center py-8">
                        <div className="text-sm" style={{ color: APP_THEME.textSecondary }}>
                            No input fields available for this operator
                        </div>
                        <button
                            onClick={() => navigate(-1)}
                            className="mt-4 px-6 py-2 rounded-lg"
                            style={{ background: APP_THEME.primaryColor, color: "white" }}
                        >
                            Go Back
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderBillDetails = () => {
        const info = {
            logo: logo ? null : getBillerLogo(biller_name),
            color: getBillerColor(biller_name)
        };

        const isEditable = billDetails.acceptPartPay === true;

        return (
            <div>
                {/* Operator Info Header */}
                <div className="flex items-center gap-3 mb-5 p-3.5 px-4 rounded-xl border"
                    style={{
                        background: APP_THEME.surfaceColor,
                        borderColor: APP_THEME.borderColor,
                    }}
                >
                    {logo ? (
                        <img
                            src={logo}
                            alt={biller_name}
                            className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                }
                            }}
                        />
                    ) : null}
                    <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-extrabold text-white flex-shrink-0"
                        style={{
                            background: info.color,
                            fontFamily: APP_THEME.fontFamily,
                            display: logo ? 'none' : 'flex'
                        }}
                    >
                        {info.logo}
                    </div>
                    <div className="flex-1">
                        <div className="text-base font-bold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            {biller_name}
                        </div>
                        <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            {category} Provider
                        </div>
                    </div>
                    <button onClick={() => {
                        setStep("input");
                        setBillDetails(null);
                        setFormData({});
                        setPaymentAmount("");
                        setAmountError("");
                    }}
                        className="bg-transparent border-none font-bold text-[13px] cursor-pointer px-3 py-1.5 rounded-lg"
                        style={{ color: APP_THEME.primaryColor, fontFamily: APP_THEME.fontFamily }}
                    >
                        Edit Details
                    </button>
                </div>

                {/* Bill Details Card */}
                <div className="rounded-2xl border p-5 mb-6"
                    style={{
                        background: APP_THEME.surfaceColor,
                        borderColor: APP_THEME.borderColor,
                    }}
                >
                    <h3 className="text-sm font-bold mb-3 pb-2 border-b" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily, borderBottomColor: APP_THEME.borderColor }}>
                        Bill Information
                    </h3>

                    {/* Consumer Number */}
                    {billDetails.cellNumber && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Consumer Number</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{billDetails.cellNumber}</span>
                        </div>
                    )}

                    {/* Consumer Name */}
                    {billDetails.userName && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Consumer Name</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{billDetails.userName}</span>
                        </div>
                    )}

                    {/* Bill Net Amount - Show prominently */}
                    {billDetails.billnetamount > 0 && (
                        <div className="flex justify-between items-center mb-3 p-2 rounded-lg" style={{ background: APP_THEME.primaryLight }}>
                            <span className="text-[14px] font-bold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>Bill Net Amount</span>
                            <span className="text-[14px] font-extrabold" style={{ color: APP_THEME.primaryColor, fontFamily: APP_THEME.fontFamily }}>₹{billDetails.billnetamount}</span>
                        </div>
                    )}

                    {/* Bill Amount */}
                    {billDetails.billAmount > 0 && billDetails.billAmount !== billDetails.billnetamount && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Bill Amount</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>₹{billDetails.billAmount}</span>
                        </div>
                    )}

                    {/* Bill Convenience Fee */}
                    {billDetails.bill_convenience_fee && parseFloat(billDetails.bill_convenience_fee) > 0 && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Convenience Fee</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>₹{billDetails.bill_convenience_fee}</span>
                        </div>
                    )}

                    {/* Balance */}
                    {billDetails.balance && parseFloat(billDetails.balance) > 0 && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Balance</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>₹{billDetails.balance}</span>
                        </div>
                    )}

                    {/* Bill Date */}
                    {billDetails.billdate && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Bill Date</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{billDetails.billdate}</span>
                        </div>
                    )}

                    {/* Due Date */}
                    {billDetails.dueDate && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Due Date</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{billDetails.dueDate}</span>
                        </div>
                    )}

                    {/* Status */}
                    {billDetails.status && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Status</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{billDetails.status}</span>
                        </div>
                    )}

                    {/* Plan Name */}
                    {billDetails.planName && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Plan Name</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{billDetails.planName}</span>
                        </div>
                    )}

                    {/* Next Recharge */}
                    {billDetails.nextRecharge && (
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-[13px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Next Recharge</span>
                            <span className="text-[13px] font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{billDetails.nextRecharge}</span>
                        </div>
                    )}

                    {/* Additional Info from additionalInfo.info */}
                    {billDetails.additionalInfo && billDetails.additionalInfo.length > 0 && (
                        <>
                            <h3 className="text-sm font-bold mt-4 mb-3 pt-2 border-t" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily, borderTopColor: APP_THEME.borderColor }}>
                                Additional Details
                            </h3>
                            {billDetails.additionalInfo.map((item, index) => (
                                <div key={index} className="flex justify-between items-center mb-2">
                                    <span className="text-[12px]" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                                        {item.infoName}
                                    </span>
                                    <span className="text-[12px] font-medium" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{item.infoValue}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Payment Amount Input */}
                <div className="rounded-2xl border p-5 mb-6"
                    style={{
                        background: APP_THEME.surfaceColor,
                        borderColor: APP_THEME.borderColor,
                    }}
                >
                    <h3 className="text-sm font-bold mb-3 pb-2 border-b" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily, borderBottomColor: APP_THEME.borderColor }}>
                        Payment Amount
                    </h3>

                    <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            Enter Amount (₹)
                            {!isEditable && <span className="text-xs ml-2" style={{ color: APP_THEME.textSecondary }}>(Fixed Amount)</span>}
                        </label>
                        <input
                            type="text"
                            value={paymentAmount}
                            onChange={(e) => handleAmountChange(e.target.value)}
                            placeholder={`Enter amount (Min: ₹${range.min}, Max: ₹${range.max})`}
                            disabled={!isEditable}
                            className={`w-full p-3.5 rounded-xl text-[15px] font-semibold outline-none transition-all duration-200 ${!isEditable ? 'opacity-70 cursor-not-allowed' : ''}`}
                            style={{
                                border: `1px solid ${amountError ? APP_THEME.errorColor : APP_THEME.borderColor}`,
                                background: APP_THEME.surfaceColor,
                                color: APP_THEME.textPrimary,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                            onFocus={(e) => !amountError && (e.target.style.borderColor = APP_THEME.primaryColor)}
                            onBlur={(e) => !amountError && (e.target.style.borderColor = APP_THEME.borderColor)}
                        />
                        {amountError && (
                            <div className="text-xs mt-1.5 font-medium" style={{ color: APP_THEME.errorColor }}>
                                {amountError}
                            </div>
                        )}
                        <div className="text-xs mt-1.5" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            Range: ₹{range.min} - ₹{range.max}
                            {!isEditable && " (Partial payment not allowed)"}
                        </div>
                    </div>
                </div>

                {/* Payment Status */}
                <div className="mb-4 px-1">
                    <div className="text-xs flex items-center gap-2 flex-wrap" style={{ color: APP_THEME.textSecondary }}>
                        <span>Payment:</span>
                        {billDetails.acceptPayment ? (
                            <span className="text-green-600 font-semibold">✓ Accepted</span>
                        ) : (
                            <span className="text-red-600 font-semibold">✗ Not Accepted</span>
                        )}
                        {billDetails.acceptPartPay && (
                            <span className="text-blue-600 font-semibold">• Partial Payment Allowed</span>
                        )}
                        {!billDetails.acceptPartPay && (
                            <span className="text-gray-500 font-semibold">• Full Payment Required</span>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                    <button
                        onClick={() => {
                            setStep("input");
                            setBillDetails(null);
                            setPaymentAmount("");
                            setAmountError("");
                        }}
                        className="flex-1 h-12 bg-transparent rounded-lg text-base font-bold cursor-pointer transition-all duration-200 border"
                        style={{
                            color: APP_THEME.textSecondary,
                            borderColor: APP_THEME.borderColor,
                            fontFamily: APP_THEME.fontFamily,
                        }}
                    >
                        Back
                    </button>
                    <button
                        onClick={handlePay}
                        disabled={!billDetails.acceptPayment || !!amountError || !paymentAmount || parseFloat(paymentAmount) <= 0}
                        className="flex-1 h-12 text-white border-none rounded-lg text-base font-bold cursor-pointer transition-all duration-200 active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                            background: APP_THEME.primaryColor,
                            borderRadius: APP_THEME.borderRadiusBtn,
                            fontFamily: APP_THEME.fontFamily,
                        }}
                    >
                        Pay Now ₹{paymentAmount ? parseFloat(paymentAmount).toLocaleString() : '0'}
                    </button>
                </div>
            </div>
        );
    };

    // If no operator data, show error
    if (!operator_id) {
        return (
            <>
                <Header title="Bill Payment" showBack={true} />
                <div className="p-4 text-center py-12">
                    <div className="text-red-500">No operator selected. Please go back and try again.</div>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-4 px-6 py-2 rounded-lg"
                        style={{ background: APP_THEME.primaryColor, color: "white" }}
                    >
                        Go Back
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <Header title={`Pay ${category || "Bill"}`} showBack={true} />
            <div className="p-4 pb-8">
                {loadingForm ? (
                    <div className="text-center py-12">
                        <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${APP_THEME.primaryColor}33`, borderTopColor: APP_THEME.primaryColor }} />
                        <div className="text-sm font-medium" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            Loading form fields...
                        </div>
                    </div>
                ) : (
                    <>
                        {step === "input" && renderInputForm()}
                        {step === "fetched" && renderBillDetails()}
                    </>
                )}

                <Toaster position="bottom-center" />

                <style>
                    {`
                        .active\\:scale-98:active {
                            transform: scale(0.98);
                        }
                        @keyframes spin {
                            to { transform: rotate(360deg); }
                        }
                        .animate-spin {
                            animation: spin 1s linear infinite;
                        }
                    `}
                </style>
            </div>
        </>
    );
}

export default OperatorForm;